import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isFuture } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '@sorare/core/src/__generated__/globalTypes';
import {
  ManagerProgressionTaskSlug,
  ManagerProgressionTaskState,
  ManagerTaskPeriodicity,
  TaskGenre,
} from '@sorare/core/src/__generated__/globalTypes';
import { Clock } from '@sorare/core/src/atoms/icons/Clock';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandXXS, LabelS } from '@sorare/core/src/atoms/typography';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType, withFragments } from '@sorare/core/src/lib/gql';

import { AnyTask } from './AnyTask/index.tsx';
import { DailyActionTask } from './DailyActionTask/index.tsx';
import { StreakTask } from './StreakTask/index.tsx';
import { TaskClaimedDialogs } from './TaskClaimedDialogs/index.tsx';
import { ThresholdsDuoTaskCampaign } from './ThresholdsDuoTaskCampaign/index.tsx';
import type {
  getTasksSections_dailyActionTaskInterface,
  getTasksSections_taskGroup,
} from './__generated__/getTasksSections.graphql.ts';
import { hasEditableTaskAppearance, isTaskLive } from './utils.ts';

const CapitalizedLabel = styled(LabelS)`
  text-transform: capitalize;
`;

type Task = getTasksSections_taskGroup['myTasks'][number];

type GetTasksSectionsArgs = {
  sport: Sport;
  taskGroup?: getTasksSections_taskGroup;
  isLaptop?: boolean;
  onDropChallengeClick: (task: Task) => () => void;
  onDecisivePlayerPickerClick: Nullable<() => void>;
  onClaim: (task: Task) => () => void;
  loadingTaskId?: string | false;
};

export const getTasksSections = withFragments(
  ({
    taskGroup,
    isLaptop,
    onDropChallengeClick,
    onDecisivePlayerPickerClick,
    onClaim,
    loadingTaskId,
  }: GetTasksSectionsArgs) => {
    const { myTasks: tasks = [] } = taskGroup || {};

    const unclaimedTasks = tasks.filter(task => {
      const { aasmState, name } = task;
      return (
        aasmState === ManagerProgressionTaskState.COMPLETED &&
        name !== ManagerProgressionTaskSlug.DAILY_ACTION &&
        !hasEditableTaskAppearance(task)
      );
    });
    const claimedTasks = tasks.filter(
      ({ aasmState, name }) =>
        aasmState === ManagerProgressionTaskState.CLAIMED &&
        name !== ManagerProgressionTaskSlug.DAILY_ACTION
    );
    const nonPeriodicTasks = tasks.filter(
      task =>
        task.periodicity === null &&
        task.aasmState === ManagerProgressionTaskState.READY &&
        isType(task, 'DecisivePlayerPickerTask')
    );

    const dailyActionTask = tasks.find(
      ({ name }) => name === ManagerProgressionTaskSlug.DAILY_ACTION
    );

    const dailyTasks = tasks.filter(task => {
      const { aasmState, name, periodicity } = task;
      return (
        periodicity === ManagerTaskPeriodicity.DAILY &&
        (aasmState !== ManagerProgressionTaskState.COMPLETED ||
          hasEditableTaskAppearance(task)) &&
        name !== ManagerProgressionTaskSlug.DAILY_ACTION
      );
    });

    const dropChallengeTask = dailyTasks.find(
      task => task.name === ManagerProgressionTaskSlug.DROP_CHALLENGE
    );

    const onboardingTasks = tasks.filter(
      ({ genre, aasmState }) =>
        genre === TaskGenre.ONBOARDING_COMMON_GAME &&
        aasmState === ManagerProgressionTaskState.READY
    );

    const { campaigns } = taskGroup || {};

    const {
      live: activeGameWeeklyLiveTasks,
      upcoming: gameWeeklyUpcomingTasks,
    } = tasks
      .filter(task => isType(task, 'GameWeeklyTask'))
      .reduce<{
        live: Extract<Task, { __typename: 'GameWeeklyTask' }>[];
        upcoming: Extract<Task, { __typename: 'GameWeeklyTask' }>[];
      }>(
        (obj, task) => {
          if (
            isTaskLive(task) &&
            task.aasmState === ManagerProgressionTaskState.READY
          ) {
            obj.live.push(task);
          } else if (!isTaskLive(task)) {
            obj.upcoming.push(task);
          }
          return obj;
        },
        { live: [], upcoming: [] }
      );

    const cardCollectionTasks = tasks.filter(
      task =>
        isType(task, 'CardsCountTask') &&
        task.aasmState === ManagerProgressionTaskState.READY
    );
    const collectionCompletionTasks = tasks.filter(
      task =>
        isType(task, 'CompleteCollectionsCountTask') &&
        task.aasmState === ManagerProgressionTaskState.READY
    );
    const streakTasks = sortBy(
      t => (isType(t, 'ThresholdsStreakTask') && t.engaged ? -1 : 1),
      [
        ...tasks.filter(
          task =>
            isType(task, 'ThresholdsStreakTask') &&
            [
              ManagerProgressionTaskState.READY,
              ManagerProgressionTaskState.CLAIMED,
            ].includesValue(task.aasmState)
        ),
      ]
    );

    const thresholdsDuoConnectionTasks = tasks.filter(
      task =>
        isType(task, 'ThresholdsDuoConnectionTask') &&
        [
          ManagerProgressionTaskState.READY,
          ManagerProgressionTaskState.ASSIGNED,
        ].includesValue(task.aasmState)
    );

    return [
      {
        id: 'unclaimed',
        show: !!unclaimedTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Unclaimed Rewards" />
          </BrandXXS>
        ),
        content: (
          <Scrollable itemToDisplay={isLaptop ? 2 : 1} overhang>
            {unclaimedTasks?.map(unclaimedTask => (
              <AnyTask
                key={unclaimedTask.id}
                task={unclaimedTask}
                onClaim={onClaim(unclaimedTask)}
                loading={loadingTaskId === unclaimedTask.id}
              />
            ))}
          </Scrollable>
        ),
      },
      {
        id: 'onboarding',
        show: !!onboardingTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Onboarding" />
          </BrandXXS>
        ),
        subtitle: <FormattedMessage defaultMessage="Learn the game" />,
        content: onboardingTasks?.map(onboardingTask => (
          <AnyTask key={onboardingTask.id} task={onboardingTask} />
        )),
      },
      {
        id: 'daily-action',
        show: !!dailyActionTask,
        content: (
          <DailyActionTask
            task={dailyActionTask!}
            onDropChallengeClick={
              dropChallengeTask
                ? onDropChallengeClick(dropChallengeTask)
                : undefined
            }
            onDecisivePlayerPickerClick={onDecisivePlayerPickerClick}
            showTitle
          />
        ),
      },
      {
        id: 'daily',
        show: !!dailyTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Daily" />
          </BrandXXS>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Fresh challenges every day" />
        ),
        content: dailyTasks?.map(dailyTask => {
          return (
            <AnyTask
              key={dailyTask.id}
              task={dailyTask}
              onDropChallengeClick={
                dropChallengeTask
                  ? onDropChallengeClick(dropChallengeTask)
                  : undefined
              }
              onClaim={onClaim(dailyTask)}
              loading={loadingTaskId === dailyTask.id}
            />
          );
        }),
      },
      {
        id: 'weekly',
        show: !!nonPeriodicTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Weekly" />
          </BrandXXS>
        ),
        subtitle:
          taskGroup?.slug === 'collect' ? (
            <FormattedMessage defaultMessage="Discover fresh weekly scouting missions every Tuesday!" />
          ) : (
            <FormattedMessage defaultMessage="Bigger goals, bigger prizes" />
          ),
        content: nonPeriodicTasks?.map(nonPeriodicTask => {
          return (
            <AnyTask
              key={nonPeriodicTask.id}
              task={nonPeriodicTask}
              onDropChallengeClick={
                dropChallengeTask
                  ? onDropChallengeClick(dropChallengeTask)
                  : undefined
              }
              onClaim={onClaim(nonPeriodicTask)}
              loading={loadingTaskId === nonPeriodicTask.id}
            />
          );
        }),
      },
      {
        id: 'market',
        show: false,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Market" />
          </BrandXXS>
        ),
        subtitle: <FormattedMessage defaultMessage="Purchase & prosper" />,
        content: null,
      },
      {
        id: 'duo-campaigns',
        show: !!campaigns?.length,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Connect with friends" />
          </BrandXXS>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Earn rewards for referring friends" />
        ),
        content: (campaigns || []).map(campain => (
          <ThresholdsDuoTaskCampaign key={campain.slug} campain={campain} />
        )),
      },
      {
        id: 'duo-tasks-inprogress',
        show: !!thresholdsDuoConnectionTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Connect with friends" />
          </BrandXXS>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Earn rewards for referring friends" />
        ),
        content: thresholdsDuoConnectionTasks.map(
          thresholdsDuoConnectionTask => (
            <AnyTask
              key={thresholdsDuoConnectionTask.id}
              task={thresholdsDuoConnectionTask}
            />
          )
        ),
      },
      {
        id: 'streak',
        show: !!streakTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Streaks" />
          </BrandXXS>
        ),
        subtitle: <FormattedMessage defaultMessage="Heat up and stay hot" />,
        content: streakTasks?.map(streakTask => (
          <StreakTask key={streakTask.id} streakTask={streakTask} />
        )),
      },
      {
        id: 'weekly-live',
        show: !!activeGameWeeklyLiveTasks?.length,
        title: (
          <Horizontal>
            <BrandXXS>
              <FormattedMessage defaultMessage="Game Week" />
            </BrandXXS>
            {!!activeGameWeeklyLiveTasks[0]?.so5Fixture.endDate &&
              isFuture(activeGameWeeklyLiveTasks[0].so5Fixture.endDate) && (
                <CapitalizedLabel>
                  <TimeLeft
                    Layout={({ children }) => (
                      <Horizontal as="span" gap={0.5}>
                        <Clock />
                        <CapitalizedLabel color="var(--c-yellow-400)" bold>
                          {children}
                        </CapitalizedLabel>
                      </Horizontal>
                    )}
                    time={activeGameWeeklyLiveTasks[0].so5Fixture.endDate}
                  />
                </CapitalizedLabel>
              )}
          </Horizontal>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Fresh challenges every Game Week" />
        ),
        content: activeGameWeeklyLiveTasks?.map(activeGameWeeklyLiveTask => (
          <AnyTask
            key={activeGameWeeklyLiveTask.id}
            task={activeGameWeeklyLiveTask}
          />
        )),
      },
      {
        id: 'weekly-upcoming',
        show: !!gameWeeklyUpcomingTasks?.length,
        title: (
          <Horizontal>
            <BrandXXS>
              <FormattedMessage defaultMessage="Upcoming Game Week" />
            </BrandXXS>
            {!!gameWeeklyUpcomingTasks[0] && (
              <TimeLeft
                Layout={({ children }) => (
                  <CapitalizedLabel color="var(--c-onBase-success)" bold>
                    <FormattedMessage
                      defaultMessage="Starts in {time}"
                      values={{ time: children }}
                    />
                  </CapitalizedLabel>
                )}
                time={gameWeeklyUpcomingTasks[0].so5Fixture.startDate}
              />
            )}
          </Horizontal>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Fresh challenges every Game Week" />
        ),
        content: gameWeeklyUpcomingTasks?.map(gameWeeklyUpcomingTask => (
          <AnyTask
            key={gameWeeklyUpcomingTask.id}
            task={gameWeeklyUpcomingTask}
          />
        )),
      },
      {
        id: 'collection',
        show: !!cardCollectionTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage {...fantasy.collectionMilestones} />
          </BrandXXS>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Collect as many cards as you can" />
        ),
        content: cardCollectionTasks?.map(cardCollectionTask => (
          <AnyTask key={cardCollectionTask.id} task={cardCollectionTask} />
        )),
      },
      {
        id: 'completion',
        show: !!collectionCompletionTasks?.length,
        title: (
          <BrandXXS>
            <FormattedMessage {...fantasy.completionMilestones} />
          </BrandXXS>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Complete collections of your favorite teams" />
        ),
        content: collectionCompletionTasks?.map(collectionCompletionTask => (
          <AnyTask
            key={collectionCompletionTask.id}
            task={collectionCompletionTask}
          />
        )),
      },
      {
        id: 'fantasy',
        show: false,
        title: (
          <BrandXXS>
            <FormattedMessage defaultMessage="Fantasy milestones" />
          </BrandXXS>
        ),
        subtitle: (
          <FormattedMessage defaultMessage="Play consistently and win rewards" />
        ),
        content: null,
      },
      {
        id: 'claimed',
        show: !!claimedTasks?.length,
        expandedByDefault: false,
        title: (
          <BrandXXS>
            <FormattedMessage {...glossary.claimed} />
          </BrandXXS>
        ),
        content: claimedTasks?.map(claimedTask => (
          <AnyTask key={claimedTask.id} task={claimedTask} />
        )),
      },
    ];
  },
  {
    taskGroup: gql`
      fragment getTasksSections_taskGroup on TaskGroup {
        slug
        campaigns(sport: $sport, rarity: $rarity) {
          slug
          ...ThresholdsDuoTaskCampaign_taskCampaign
        }
        myTasks(sport: $sport, rarity: $rarity) {
          id
          aasmState
          periodicity
          genre
          ... on GameWeeklyTask {
            id
            so5Fixture {
              slug
              startDate
              endDate
            }
          }
          ... on ThresholdsStreakTask {
            id
            engaged
          }
          ... on ThresholdsDuoConnectionTask {
            id
          }
          ...AnyTask_taskInterface
          ...isTaskLive_gameWeeklyTask
          ...TaskClaimedDialogs_taskInterface
          ...DailyActionTask_taskInterface
          ...hasEditableTaskAppearance_taskInterface
        }
      }
      ${AnyTask.fragments.taskInterface}
      ${isTaskLive.fragments.gameWeeklyTask}
      ${ThresholdsDuoTaskCampaign.fragments.taskCampaign}
      ${hasEditableTaskAppearance.fragments.taskInterface}
      ${/* oxlint-disable-next-line sorare/no-detached-fragments */ ''}
      ${TaskClaimedDialogs.fragments.taskInterface}
      ${DailyActionTask.fragments.taskInterface}
    ` as TypedDocumentNode<getTasksSections_taskGroup>,
    dailyActionTaskInterface: gql`
      fragment getTasksSections_dailyActionTaskInterface on TaskInterface {
        id
        ...DailyActionTask_taskInterface
      }
      ${DailyActionTask.fragments.taskInterface}
    ` as TypedDocumentNode<getTasksSections_dailyActionTaskInterface>,
  }
);
